// ============================================
// Header.js
// ヘッダー関連の 3 つの機能をまとめたモジュール
//
// 機能一覧:
//   1. fixedHeader  : スクロール量に応じてヘッダーの表示状態を切り替える
//   2. drawer       : ハンバーガーメニュー（ドロワー）の開閉
//   3. megaMenu     : PC グローバルナビのメガメニュー（ホバー開閉）
//
// 依存:
//   - MicroModal : https://micromodal.vercel.app/
//   - Lenis      : Scroll.js からインポート（スクロールロック用）
// ============================================

import MicroModal from 'micromodal';
import { lenis } from './Scroll';
import { rafThrottle } from '../utils/rafThrottle';

// ============================================
// 設定値
// ============================================
const CONFIG = {
  // --- fixedHeader ---

  // ヘッダーに付与する CSS クラス名
  header_activeClass: 'is-active', // ヒーローを通過後に fixed ヘッダーを表示
  header_hiddenClass: 'is-hidden', // スクロール開始直後にヘッダーを隠す
  header_openClass: 'is-open',     // ドロワーが開いているときのクラス

  // --- drawer ---

  // ドロワーを開く/閉じるハンバーガーボタンのクラス
  hamburger_openClass: 'is-open',

  // MicroModal で制御するドロワーの ID（header.php の id="navigation" に対応）
  drawer_modalId: 'navigation',
};

// ============================================
// 1. fixedHeader — スクロールに応じたヘッダー表示制御
//
// 動作:
//   ・スクロール量 ≤ ヘッダー高さ         → 通常表示（クラスなし）
//   ・ヘッダー高さ < スクロール量 ≤ ヒーロー高さ → is-hidden（ヘッダーを隠す）
//   ・スクロール量 > ヒーロー高さ          → is-active（fixed ヘッダーを表示）
//
// 対象要素:
//   .js-header : ヘッダー要素
//   .js-hero   : ヒーロー（メインビジュアル）要素
// ============================================
function fixedHeader() {
  const header = document.querySelector('.js-header');
  const hero = document.querySelector('.js-hero');

  // ヒーローがないページ（下層ページ等）では何もしない
  if (!header || !hero) return;

  // サイズキャッシュ（毎スクロールで計測しないようパフォーマンスを確保）
  let headerH = header.clientHeight;
  let heroH = hero.clientHeight;

  // ウィンドウリサイズ時にキャッシュを更新する
  const updateSizeCache = () => {
    headerH = header.clientHeight;
    heroH = hero.clientHeight;
  };

  // スクロール量に応じてヘッダーのクラスを付け替える
  const updateHeaderState = () => {
    const scrollY = window.scrollY;

    // ヒーローを通過したら is-active（fixed ヘッダー表示）
    header.classList.toggle(CONFIG.header_activeClass, scrollY > heroH);

    // ヘッダー高さを超えたがヒーローはまだ通過していない区間は is-hidden
    header.classList.toggle(CONFIG.header_hiddenClass, scrollY > headerH && scrollY <= heroH);
  };

  // scroll / resize イベントを rAF で間引く（rafThrottle で共通化）。
  // リサイズ時は clientHeight の再計測（リフロー）も rAF 内にまとめて強制レイアウトを防ぐ。
  const requestUpdate = rafThrottle(updateHeaderState);
  const requestResizeUpdate = rafThrottle(() => {
    updateSizeCache();
    updateHeaderState();
  });

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestResizeUpdate, { passive: true });

  // 初期状態を即座に反映（ページ途中からロードされた場合に備える）
  updateHeaderState();
}

// ============================================
// 2. drawer — ハンバーガーメニューの開閉
//
// MicroModal でドロワーを制御しつつ、Lenis のスクロールを
// ドロワーが開いているときにロック・閉じたときに解除する。
//
// CSS カスタムプロパティ連携:
//   --wide-hamburger    : 'true'  → 全幅でハンバーガー表示
//                         'false' → スマホのみハンバーガー（PC はグローバルナビ）
//   --hamburger-breakpoint : ハンバーガー表示の画面幅しきい値（例: '1024px'）
// ============================================
function drawer() {
  // MicroModal を初期化（drawer・megaMenu 両方で使うがここで一度だけ呼ぶ）
  MicroModal.init();

  const header = document.querySelector('.js-header');
  const hamburger = document.querySelector('.js-drawer-hamburger');
  const navigation = document.querySelector('.js-drawer-navigation');

  if (!hamburger || !navigation) return;

  const menuWrap = navigation.querySelector('.l-drawer__nav');
  const menu = navigation.querySelector('.l-drawer__list');

  if (!menu || !menuWrap) return;

  // CSS カスタムプロパティからハンバーガーの表示設定を取得する
  const styles = getComputedStyle(document.documentElement);
  const wideHamburger = styles.getPropertyValue('--wide-hamburger').trim();
  const breakpoint = styles.getPropertyValue('--hamburger-breakpoint').trim() || '1024px';
  const mediaQuery = window.matchMedia(`(max-width: ${breakpoint})`);

  // ドロワー内のスクロールを親（Lenis）に伝播させないためのリスナー
  // ドロワー内でスクロールしても、背景がスクロールしないようにする
  const stopScrollPropagation = (e) => e.stopPropagation();
  const passiveOpts = { passive: true };
  let scrollBound = false;

  const bindDrawerScroll = () => {
    if (scrollBound) return;
    menuWrap.addEventListener('wheel', stopScrollPropagation, passiveOpts);
    menuWrap.addEventListener('touchmove', stopScrollPropagation, passiveOpts);
    scrollBound = true;
  };

  const unbindDrawerScroll = () => {
    if (!scrollBound) return;
    menuWrap.removeEventListener('wheel', stopScrollPropagation, passiveOpts);
    menuWrap.removeEventListener('touchmove', stopScrollPropagation, passiveOpts);
    scrollBound = false;
  };

  // --wide-hamburger が 'false' → スマホのみハンバーガー（PC はグローバルナビで操作）
  // --wide-hamburger が 'false' 以外（'true' や未設定）→ 常にハンバーガー表示
  if (wideHamburger === 'false') {
    applyResponsiveDrawer(mediaQuery);

    // matchMedia の変更検知（画面幅変化 = リサイズ時）
    // addListener は非推奨のため addEventListener で分岐する
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', applyResponsiveDrawer);
    } else {
      mediaQuery.addListener(applyResponsiveDrawer); // Safari 旧版フォールバック
    }

    // メディアクエリの一致状態に応じてハンバーガーの有効/無効を切り替える
    function applyResponsiveDrawer(mq) {
      if (mq.matches) {
        // スマホ幅 → ハンバーガーを有効化
        hamburger.addEventListener('click', onHamburgerClick);
        bindDrawerScroll();
        navigation.setAttribute('aria-hidden', 'true');
      } else {
        // PC 幅 → ハンバーガーを無効化し、開いていたドロワーを閉じる
        navigation.querySelectorAll('a, button').forEach((el) => el.blur());
        navigation.setAttribute('aria-hidden', 'true');
        lenis.start(); // スクロールロックを解除
        if (hamburger.classList.contains(CONFIG.hamburger_openClass)) {
          hamburger.classList.remove(CONFIG.hamburger_openClass);
          hamburger.setAttribute('aria-expanded', 'false');
        }
        unbindDrawerScroll();
        hamburger.removeEventListener('click', onHamburgerClick);
      }
    }
  } else {
    // 常にハンバーガーを有効化（全幅ハンバーガー構成）
    hamburger.addEventListener('click', onHamburgerClick);
    if (wideHamburger === 'true') {
      bindDrawerScroll();
    }
    navigation.setAttribute('aria-hidden', 'true');
  }

  // ハンバーガーボタンがクリックされたときの処理
  function onHamburgerClick() {
    const isOpen = navigation.getAttribute('aria-hidden') === 'false';
    const menuTxt = document.querySelector('.js-drawer-hamburger .-txt');

    if (isOpen) {
      // ドロワーを閉じる
      closeDrawer();
      if (menuTxt) menuTxt.textContent = 'Menu';
    } else {
      // ドロワーを開く
      openDrawer();
      if (menuTxt) menuTxt.textContent = 'Close';
    }
  }

  function openDrawer() {
    header?.classList.add(CONFIG.header_openClass);
    hamburger.classList.add(CONFIG.hamburger_openClass);
    hamburger.setAttribute('aria-expanded', 'true');
    lenis.stop(); // 背景のスクロールを止める

    MicroModal.show(CONFIG.drawer_modalId, {
      onClose: () => {
        // モーダルの外側クリックや ESC キーで閉じた場合も状態をリセット
        header?.classList.remove(CONFIG.header_openClass);
        hamburger.classList.remove(CONFIG.hamburger_openClass);
        hamburger.setAttribute('aria-expanded', 'false');
        lenis.start();
      },
    });
  }

  function closeDrawer() {
    hamburger.classList.remove(CONFIG.hamburger_openClass);
    hamburger.setAttribute('aria-expanded', 'false');
    header?.classList.remove(CONFIG.header_openClass);
    lenis.start();
    MicroModal.close(CONFIG.drawer_modalId);
  }

  // ドロワー内のリンクをクリックしたとき自動でドロワーを閉じる
  navigation.querySelectorAll('.l-drawer__link').forEach((link) => {
    link.addEventListener('click', () => {
      if (navigation.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });
  });
}

// ============================================
// 3. megaMenu — PC グローバルナビのメガメニュー（ホバー開閉）
//
// 対象要素:
//   .l-navigation__link              : ナビリンク全般（ホバーで他のポップアップを閉じる）
//   .l-navigation__link.js-popup-trigger : ホバーでポップアップを開くトリガー
//   .l-navigation__child             : ポップアップコンテンツ
//
// aria-controls 属性でトリガーとポップアップを紐付ける
// 例: <a class="js-popup-trigger" aria-controls="mega-menu-01">
//     <div id="mega-menu-01" class="l-navigation__child">
// ============================================
function megaMenu() {
  const allLinks = document.querySelectorAll('.l-navigation__link');
  const triggers = document.querySelectorAll('.l-navigation__link.js-popup-trigger');
  const popups = document.querySelectorAll('.l-navigation__mega');

  if (allLinks.length === 0 || triggers.length === 0 || popups.length === 0) return;

  // aria-controls が設定されているリンクの ID 一覧を収集しておく（一括クローズ用）
  const managedModalIds = [];
  allLinks.forEach((link) => {
    const id = link.getAttribute('aria-controls');
    if (id) managedModalIds.push(id);
  });

  // 開いているポップアップをすべて閉じる（ホバー / フォーカス / Escape で共用）
  const closeAllPopups = () => {
    managedModalIds.forEach((id) => {
      const modal = document.getElementById(id);
      if (modal?.classList.contains('is-open')) MicroModal.close(id);
    });
  };

  // 任意のナビリンクにホバー / フォーカスしたとき、開いている他のポップアップを閉じる
  allLinks.forEach((link) => {
    link.addEventListener('mouseenter', closeAllPopups);
    // トリガー以外の通常リンクにフォーカスが乗ったら閉じる
    // （トリガー自身は下で「自分のポップアップを開く」処理を行うため除外）
    if (!link.classList.contains('js-popup-trigger')) {
      link.addEventListener('focus', closeAllPopups);
    }
  });

  // ポップアップのエリアからマウスが外れたときに閉じる
  popups.forEach((popup) => {
    popup.addEventListener('mouseleave', () => {
      const id = popup.id;
      if (id) MicroModal.close(id);
    });
  });

  // トリガーにホバー / フォーカスしたときポップアップを開く
  const openPopup = (id, { keepFocus = false } = {}) => {
    const modal = document.getElementById(id);
    if (!modal || modal.classList.contains('is-open')) return;

    MicroModal.show(id);

    // ホバー時は内部リンクがフォーカスを奪わないよう blur する。
    // キーボード操作時（keepFocus）はフォーカス移動を妨げないため blur しない。
    if (!keepFocus) {
      const firstLink = modal.querySelector('.l-navigation__list-child-link');
      firstLink?.blur();
    }
  };

  triggers.forEach((trigger) => {
    const id = trigger.getAttribute('aria-controls');
    if (!id) return;

    trigger.addEventListener('mouseenter', () => openPopup(id));
    // キーボードでトリガーにフォーカスしたら、他を閉じて自分のポップアップを開く
    trigger.addEventListener('focus', () => {
      closeAllPopups();
      openPopup(id, { keepFocus: true });
    });
  });

  // Escape キーでポップアップを閉じ、開いていたトリガーへフォーカスを戻す
  const navContainer = triggers[0].closest('.l-navigation') || document;
  navContainer.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openTrigger = Array.from(triggers).find((t) => {
      const cid = t.getAttribute('aria-controls');
      return cid && document.getElementById(cid)?.classList.contains('is-open');
    });
    if (!openTrigger) return;
    closeAllPopups();
    openTrigger.focus();
  });
}

export { fixedHeader, drawer, megaMenu };
